/**
 * SEO Meta Tags Generator
 * Generates meta tags for pages including Open Graph and Twitter Cards
 */

import type { Product, Collection } from '../shopify/types';

const SITE_URL = import.meta.env.VITE_PUBLIC_STORE_URL || 'https://www.itremarketing.co.uk';
const SITE_NAME = 'IT Remarketing';
const DEFAULT_DESCRIPTION = 'Professional communication headsets from leading brands. Next day delivery, 30-day returns, and expert advice on Jabra, Poly, and Axtel headsets.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

export interface MetaTags {
  title: string;
  description: string;
  canonical: string;
  openGraph: {
    title: string;
    description: string;
    url: string;
    type: string;
    image: string;
    imageAlt: string;
    siteName: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
    image: string;
    imageAlt: string;
  };
  robots?: string;
  alternates?: Array<{ hreflang: string; href: string }>;
}

// Homepage meta tags
export function generateHomeMetaTags(): MetaTags {
  const title = `${SITE_NAME} | Professional Headsets for Business & Remote Work`;
  const description = DEFAULT_DESCRIPTION;

  return {
    title,
    description,
    canonical: SITE_URL,
    openGraph: {
      title,
      description,
      url: SITE_URL,
      type: 'website',
      image: DEFAULT_IMAGE,
      imageAlt: 'Professional headsets from IT Remarketing',
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      image: DEFAULT_IMAGE,
      imageAlt: 'Professional headsets from IT Remarketing',
    },
  };
}

// Product page meta tags
export function generateProductMetaTags(product: Product): MetaTags {
  const seoTitle = product.seo?.title || product.title;
  const seoDescription = product.seo?.description || product.description?.slice(0, 160);
  const title = `${seoTitle} | ${SITE_NAME}`;
  const description = seoDescription || `Buy ${product.title} from ${product.vendor}. ${DEFAULT_DESCRIPTION}`;
  const url = `${SITE_URL}/products/${product.handle}`;
  const image = product.featuredImage?.url || DEFAULT_IMAGE;
  const imageAlt = product.featuredImage?.altText || product.title;

  const price = product.priceRange.minVariantPrice;

  return {
    title,
    description,
    canonical: url,
    openGraph: {
      title: seoTitle,
      description,
      url,
      type: 'og:product',
      image,
      imageAlt,
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary_large_image',
      title: seoTitle,
      description,
      image,
      imageAlt,
    },
  };
}

// Generate product Open Graph product tags
export function generateProductOGTags(product: Product) {
  const price = product.priceRange.minVariantPrice;
  const variant = product.variants.edges[0]?.node;

  return {
    'product:price:amount': price.amount,
    'product:price:currency': price.currencyCode,
    'product:availability': product.availableForSale ? 'in stock' : 'out of stock',
    'product:condition': (() => {
      const mf = product.metafields?.find((m) => m?.namespace === 'mm-google-shopping' && m?.key === 'condition');
      const condition = mf?.value?.toLowerCase() || '';
      if (condition === 'refurbished' || condition === 'used') return condition;
      if (product.tags?.some((t) => t.toLowerCase() === 'refurbished')) return 'refurbished';
      if (product.title?.toLowerCase().includes('refurbished')) return 'refurbished';
      return 'new';
    })(),
    'product:brand': product.vendor,
    ...(variant?.sku && { 'product:retailer_item_id': variant.sku }),
  };
}

// Descriptive collection title builder
// Turns short collection names into SEO-friendly titles like "Jabra Wireless Headsets"
function buildDescriptiveCollectionTitle(collection: Collection): string {
  // Use Shopify SEO title if set (store owner has customised it)
  if (collection.seo?.title) return collection.seo.title;

  const handle = collection.handle;
  const name = collection.title;

  // Handle-specific descriptive titles
  const COLLECTION_TITLES: Record<string, string> = {
    'jabra': 'Jabra Professional Headsets',
    'poly': 'Poly Professional Headsets',
    'polycom': 'Poly Professional Headsets',
    'axtel': 'Axtel Professional Headsets',
    'wireless': 'Wireless Professional Headsets',
    'wireless-headsets': 'Wireless Professional Headsets',
    'wired': 'Wired Professional Headsets',
    'usb-headset': 'USB Professional Headsets',
    'usb-a': 'USB-A Wired Headsets',
    'usb-c': 'USB-C Wired Headsets',
    'bluetooth': 'Bluetooth Wireless Headsets',
    'bluetooth-wireless': 'Bluetooth Wireless Headsets',
    'dect-wireless': 'DECT Wireless Headsets',
    'dual-connectivity': 'Dual Connectivity Headsets',
    'teams-certified': 'Microsoft Teams Certified Headsets',
    'zoom-certified': 'Zoom Certified Headsets',
    'zoom-teams-headsets': 'Teams & Zoom Compatible Headsets',
    'active-noise-cancelling': 'Active Noise Cancelling Headsets',
    'office-call-centre': 'Office & Call Centre Headsets',
    'work-from-home': 'Work from Home Headsets',
    'all-headsets': 'All New Professional Headsets',
    'all-products': 'Shop All Headsets',
    'refurbished': 'Refurbished Professional Headsets',
  };

  if (COLLECTION_TITLES[handle]) return COLLECTION_TITLES[handle];

  // Fallback: append "Headsets" if not already in the name
  if (/headset/i.test(name)) return name;
  return `${name} Headsets`;
}

// Collection page meta tags
export function generateCollectionMetaTags(collection: Collection): MetaTags {
  const descriptiveTitle = buildDescriptiveCollectionTitle(collection);
  const seoDescription = collection.seo?.description || collection.description?.slice(0, 160);
  const title = `${descriptiveTitle} | ${SITE_NAME}`;
  const description = seoDescription || `Shop ${descriptiveTitle} at IT Remarketing. ${DEFAULT_DESCRIPTION}`;
  const url = `${SITE_URL}/collections/${collection.handle}`;
  const image = collection.image?.url || DEFAULT_IMAGE;
  const imageAlt = collection.image?.altText || collection.title;

  return {
    title,
    description,
    canonical: url,
    openGraph: {
      title: descriptiveTitle,
      description,
      url,
      type: 'website',
      image,
      imageAlt,
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary_large_image',
      title: descriptiveTitle,
      description,
      image,
      imageAlt,
    },
  };
}

// Search page meta tags
export function generateSearchMetaTags(query: string, totalResults: number): MetaTags {
  const title = `Search results for "${query}" | ${SITE_NAME}`;
  const description = `Found ${totalResults} results for "${query}". Shop professional headsets from leading brands.`;
  const url = `${SITE_URL}/search?q=${encodeURIComponent(query)}`;

  return {
    title,
    description,
    canonical: url,
    openGraph: {
      title,
      description,
      url,
      type: 'website',
      image: DEFAULT_IMAGE,
      imageAlt: 'Search IT Remarketing',
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary',
      title,
      description,
      image: DEFAULT_IMAGE,
      imageAlt: 'Search IT Remarketing',
    },
    robots: 'noindex, follow', // Don't index search pages
  };
}

// Static page meta tags
export function generateStaticPageMetaTags(
  pageName: string,
  customDescription?: string
): MetaTags {
  const titles: Record<string, string> = {
    about: 'About Us',
    contact: 'Contact Us',
    support: 'Support & Help',
    returns: 'Returns & Refunds',
    shipping: 'Shipping Information',
    privacy: 'Privacy Policy',
    terms: 'Terms & Conditions',
  };

  const title = `${titles[pageName] || pageName} | ${SITE_NAME}`;
  const description = customDescription || DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}/${pageName}`;

  return {
    title,
    description,
    canonical: url,
    openGraph: {
      title,
      description,
      url,
      type: 'website',
      image: DEFAULT_IMAGE,
      imageAlt: SITE_NAME,
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary',
      title,
      description,
      image: DEFAULT_IMAGE,
      imageAlt: SITE_NAME,
    },
  };
}

// Generate meta tags as HTML string
export function renderMetaTags(meta: MetaTags): string {
  const tags = [
    `<title>${meta.title}</title>`,
    `<meta name="description" content="${meta.description}" />`,
    `<link rel="canonical" href="${meta.canonical}" />`,

    // Open Graph
    `<meta property="og:title" content="${meta.openGraph.title}" />`,
    `<meta property="og:description" content="${meta.openGraph.description}" />`,
    `<meta property="og:url" content="${meta.openGraph.url}" />`,
    `<meta property="og:type" content="${meta.openGraph.type}" />`,
    `<meta property="og:image" content="${meta.openGraph.image}" />`,
    `<meta property="og:image:alt" content="${meta.openGraph.imageAlt}" />`,
    `<meta property="og:site_name" content="${meta.openGraph.siteName}" />`,

    // Twitter Cards
    `<meta name="twitter:card" content="${meta.twitter.card}" />`,
    `<meta name="twitter:title" content="${meta.twitter.title}" />`,
    `<meta name="twitter:description" content="${meta.twitter.description}" />`,
    `<meta name="twitter:image" content="${meta.twitter.image}" />`,
    `<meta name="twitter:image:alt" content="${meta.twitter.imageAlt}" />`,
  ];

  if (meta.robots) {
    tags.push(`<meta name="robots" content="${meta.robots}" />`);
  }

  if (meta.alternates) {
    meta.alternates.forEach((alt) => {
      tags.push(`<link rel="alternate" hreflang="${alt.hreflang}" href="${alt.href}" />`);
    });
  }

  return tags.join('\n');
}

// Hook to inject all meta tags into the DOM (for client-side SPA)
export function applyMetaTags(meta: MetaTags) {
  document.title = meta.title;

  const setMeta = (attr: string, key: string, content: string) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  setMeta('name', 'description', meta.description);

  // Open Graph
  setMeta('property', 'og:title', meta.openGraph.title);
  setMeta('property', 'og:description', meta.openGraph.description);
  setMeta('property', 'og:url', meta.openGraph.url);
  setMeta('property', 'og:type', meta.openGraph.type);
  setMeta('property', 'og:image', meta.openGraph.image);
  setMeta('property', 'og:image:alt', meta.openGraph.imageAlt);
  setMeta('property', 'og:site_name', meta.openGraph.siteName);

  // Twitter Cards
  setMeta('name', 'twitter:card', meta.twitter.card);
  setMeta('name', 'twitter:title', meta.twitter.title);
  setMeta('name', 'twitter:description', meta.twitter.description);
  setMeta('name', 'twitter:image', meta.twitter.image);
  setMeta('name', 'twitter:image:alt', meta.twitter.imageAlt);

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = meta.canonical;

  // Robots
  if (meta.robots) {
    setMeta('name', 'robots', meta.robots);
  }
}

// Generate additional SEO meta tags
export function generateAdditionalMetaTags(): string {
  return `
    <meta name="theme-color" content="#0A0A0A" />
    <meta name="msapplication-TileColor" content="#FF7400" />
    <meta name="apple-mobile-web-app-capable" content="yes" />
    <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
    <meta name="format-detection" content="telephone=no" />
    <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
    <link rel="icon" type="image/png" href="/favicon.png" />
    <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
    <link rel="manifest" href="/site.webmanifest" />
  `;
}
